<template>
  <div>
    <v-btn
        absolute top left
        color="primary"
        class="white--text"
        @click="navigateBack()">
        <v-icon large>mdi-chevron-left</v-icon>
    </v-btn>

    <div
      class="text-center mb-4" 
      :class="$vuetify.breakpoint.xs ? 'text-h6 mt-4' : 'text-h5 mt-5'"
    >
        <v-icon :large="!$vuetify.breakpoint.xs">
            mdi-chart-line
        </v-icon>
        Egg Daily
    </div>

    <v-data-table
        class="mt-5 mx-4"
        dense
        :headers="headers"
        :items="dataTableItems"
        item-key="key"
        :search="filterBy"
        multi-sort
        :items-per-page="15"
    >
      <template v-slot:top>
        <div class="mb-0 d-flex flex-wrap overflow-y-auto">
            <v-text-field
                v-model="farm"
                readonly
                class="mr-2"
                style="max-width: 102px;"
                label="Farm"
            />
            <v-text-field
                v-model="line"
                readonly
                class="mr-2"
                style="max-width: 102px;"
                label="Line"
            />
            <v-text-field
                v-model="minigen"
                readonly
                style="max-width: 102px;"
                class="mr-2"
                label="MG"
            />
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn
                        :disabled="!isOnline"
                        fab
                        v-on="on"
                        small
                        class="mx-2 mt-3"
                        @click="openConfigDialog()">
                        <v-icon>mdi-cog</v-icon>
                    </v-btn>
                </template>
                <span>Report Config</span>
            </v-tooltip>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn
                        :disabled="!isOnline || !minigen"
                        fab
                        v-on="on"
                        small
                        class="mr-2 mt-3"
                        @click="loadReport()">
                        <v-icon>mdi-reload</v-icon>
                    </v-btn>
                </template>
                <span>Load Report</span>
            </v-tooltip>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn
                        :disabled="!isOnline || !minigen"
                        fab
                        v-on="on"
                        small
                        class="mr-4 mt-3"
                        @click="downloadCsv()">
                        <v-icon>mdi-download</v-icon>
                    </v-btn>
                </template>
                <span>Export To Excel</span>
            </v-tooltip>
            <v-text-field
                class="mt-1"
                style="max-width: 210px;"
                v-model="filterBy"
                clearable
                label="Search"
                type="text"
                prepend-icon="mdi-magnify"
            />
        </div>
      </template>
      <template v-slot:[`item.isPedigree`]="{ item }">
        <div v-if="item.isPedigree"><v-icon class="pl-4" color="green">mdi-check</v-icon></div>
        <div v-else><v-icon class="pl-4" color="red">mdi-cancel</v-icon></div>
      </template>
      <template v-slot:[`item.grade`]="{ item }">
        <v-chip small
          :color="getGradeColor(item.grade)"
          dark
        >
          {{ getGradeDesc(item.grade) }}
        </v-chip>
      </template>
      <template v-slot:[`item.collectionTimes`]="{ item }">
         <v-chip small class="mr-1 my-1" v-for="time in item.collectionTimes" :key="time">{{ time }}</v-chip>
      </template>
    </v-data-table>

    <v-dialog v-model="showConfigDialog" persistent max-width="400">
      <v-card color="cardbackground">
        <v-card-title class="text-h5 mb-5"><v-icon class="mr-2">mdi-cog</v-icon>Report Configuration</v-card-title>
        <v-card-text class="d-flex">
            <v-select
                v-model="dialogFarm"
                class="mr-2"
                style="max-width: 102px;"
                label="Farm"
                 item-text="farmCode"
                 item-value="farmCode"
                :items="farms"
            />
             <v-select
                :disabled="!dialogFarm"
                v-model="dialogLine"
                class="mr-2"
                style="max-width: 102px;"
                label="Line"
                :items="farmLines"
            />
             <v-select
                :disabled="!dialogLine"
                v-model="dialogMinigen"
                style="max-width: 102px;"
                label="MG"
                :items="farmLineMgs"
            />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn large
            color="primary"
            class="white--text mb-2"
            :disabled="!dialogFarm || !dialogLine || !dialogMinigen"
            @click="loadReport(); closeConfigDialog();"
          >
            Load
          </v-btn>
          <v-btn
            large
            color="secondary"
            class="mb-2"
            @click="closeConfigDialog()"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="isBusy">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
   </div>
</template>

<script>
import eggApi from '../../api/eggApi'
import fileApi from '../../api/fileApi'
import { mapState } from 'vuex'
import json2csv from "../../utils/json2csv"

export default {
  data() {
    return {
      dataTableItems: [],
      filterBy: null,
      isBusy: false,
      farm: null,
      line: null,
      minigen: null,
      dialogFarm: null,
      dialogLine: null,
      dialogMinigen: null,
      allMgs: [],
      showConfigDialog: false
    };
  },
  async created() {
      this.farm = this.settings.currentFarm;
      this.allMgs = await eggApi.getMiniGenList();
  },
  beforeDestroy() {
  },
  computed: {
    ...mapState(['settings','isOnline']),
    farms() {
        return this.settings.farms;
    },
    headers() {
      return  [
        { text: 'Wingband', value: 'wingband', sortable: true },
        { text: 'Pedigree', value: 'isPedigree', sortable: true,},
        { text: 'Badge', value: 'badge', sortable: true },
        { text: 'Grade', value: 'grade', sortable: true },
        { text: 'Date', value: 'collectionDate', sortable: true },
        { text: 'Total', value: 'total', sortable: true, align: 'center'},
        { text: 'Time', value: 'collectionTimes', sortable: false}
      ]
    },
    farmLines() {
        let lineList = this.allMgs
            .filter(m => m.farmCode === this.dialogFarm)
            .map(m => m.lineCode);
        let uniqueLines = [...new Set(lineList)];
        let lines = uniqueLines.map(l => (
            {
                text: `  L${l}`,
                value: l
            }
        ));
        return lines;
    },
    farmLineMgs() {
        let mgList = this.allMgs
            .filter(m => m.farmCode === this.dialogFarm && m.lineCode == this.dialogLine)
            .map(m => m.miniGenerationNum)
            .sort((a,b) => a < b ? 1:-1);
        let uniqueMgs = [...new Set(mgList)];
        let lines = uniqueMgs.map(l => (
            { 
                text: `  ${l}`,
                value: l
            }
        ));
        return lines;
    }
  },
  methods: {
    closeConfigDialog() {
      this.showConfigDialog = false;
    },
    getGradeColor(grade) {
        switch (grade) {
            case 'GOOD': return 'success';
            case 'DIRTY': return 'primary';
            case 'MEMBRANE': return 'accent';
            case 'CRACKED': return 'secondary';
            default: return 'gray';
        }
    },
    getGradeDesc(grade) {
        let desc = grade.replace("_", " ").toLowerCase();
        let words = desc.split(" ");
        for (let i = 0; i < words.length; i++) {
          words[i] = words[i][0].toUpperCase() + words[i].substr(1);
        }
        desc = words.join(" ");
        return desc;
    },
    async loadReport() {
        try {
            this.isBusy = true;

            this.farm = this.dialogFarm;
            this.line = this.dialogLine;
            this.minigen = this.dialogMinigen;

            let birds = await eggApi.getEggDaily(this.farm,this.line, this.minigen);
            let birdsByDate = this.groupBirdsByDate(birds);
            this.dataTableItems = birdsByDate;
            this.isBusy = false;
        }
        catch(e) {
            this.isBusy = false;
            this.$root.showNoticeError(e);
        }
    },
    groupBirdsByDate(birdEvents) {
      var birdGroups = [];
      birdEvents.forEach (e =>
      {
        var groupItem = birdGroups.find(g=> g.mg == e.mg && g.wingband == e.wingband && g.badge == e.badge && g.grade == e.grade && g.collectionDate == e.collectionDate);
        if (!groupItem) {
          groupItem = {mg: e.mg, wingband: e.wingband, isPedigree:e.isPedigree, badge:e.badge, grade:e.grade, collectionDate:e.collectionDate, total: 0 };
          groupItem.collectionTimes = [];
          birdGroups.push(groupItem);
        }
        groupItem.total ++;
        groupItem.collectionTimes.push(e.collectionTime);
      });

      return birdGroups;
    },
    navigateBack() {
      this.$router.go(-1);
    },
    downloadCsv() {
      try {
        const items = this.dataTableItems.map(x => {
          return {
            Mg: x.mg,
            Wingband: x.wingband,
            Pedigree: x.isPedigree,
            Badge: x.badge,
            Grade: x.grade,
            'Date': x.collectionDate,
            'Total Eggs': x.total,
            'Time': x.collectionTimes.join(",")
          }
        });
        var csv = json2csv.convert(items);
        const fileName = `EggDaily-${this.farm}-L${this.line}-MG${this.minigen}.csv`;
        fileApi.downloadCsvLocal(fileName, csv);
      }
      catch (error) {
        this.$root.showNoticeError(error);
      }
    },
    openConfigDialog() {
      this.dialogFarm = this.farm;
      this.dialogLine = this.line;
      this.dialogMinigen = this.minigen;

      this.showConfigDialog = true;
    }
  }
}
</script>